import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  SURFACE_TINT,
} from 'styles/tokens';

import { useGetAdminBusinessUnitsQuery } from '../../../services/api/adminBusinessUnits.api';
import type { Integration, IntegrationExecution } from '../../../services/api/integrations.api';
import {
  useGetIntegrationErrorSummaryQuery,
  useGetIntegrationExecutionDetailQuery,
  useGetIntegrationExecutionsQuery,
  useGetIntegrationStatsQuery,
} from '../../../services/api/integrations.api';
import ErrorSummaryPanel from './components/ErrorSummaryPanel';
import ExecutionDetailDrawer from './components/ExecutionDetailDrawer';
import { getExecutionTimeColor, getSuccessRateColor, statusCodeColor } from './utils';

type TimeWindow = 'all' | '1h' | '24h' | '7d';

const WINDOW_MS: Record<Exclude<TimeWindow, 'all'>, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

interface ExecutionLogsProps {
  integrations: Integration[];
  initialIntegrationId?: number | null;
}

const MAX_REQUESTS_PER_PAGE = 50;

const headCellSx = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.7rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: alpha(NAVY, 0.55),
  borderBottom: 'none',
};

const bodyCellSx = {
  fontFamily: 'Jost, sans-serif',
  color: NAVY,
  borderBottom: 'none',
  py: 1.25,
};

// Tokenized filter control: no 1px border at rest, subtle navy focus ring.
const filterControlSx = {
  flexShrink: 0,
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE_TINT,
    borderRadius: RADIUS_ITEM,
    fontFamily: 'Jost, sans-serif',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: `1px solid ${alpha(NAVY, 0.15)}` },
  },
  '& .MuiInputLabel-root': { top: '-2px', fontFamily: 'Jost, sans-serif' },
};

const ExecutionLogs: React.FC<ExecutionLogsProps> = ({ integrations, initialIntegrationId = null }) => {
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(initialIntegrationId);
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'error'>('all');
  const [statusCodeFilter, setStatusCodeFilter] = useState<number | null>(null);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedExecution, setSelectedExecution] = useState<IntegrationExecution | null>(null);

  // Debounce the search box so we hit the backend at most every 300ms.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter change resets pagination to the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, statusCodeFilter, timeWindow, businessUnitFilter]);

  // Window preset → absolute startDate (frozen when the window changes, no drift).
  const startDate = useMemo(() => {
    if (timeWindow === 'all') return undefined;
    return new Date(Date.now() - WINDOW_MS[timeWindow]).toISOString();
  }, [timeWindow]);

  // Business units that this integration actually serves (for the BU filter).
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(undefined);
  const selectedIntegrationForBu = integrations.find((int) => int.id === selectedIntegrationId);
  const availableBusinessUnits = useMemo(() => {
    if (!businessUnits || !selectedIntegrationForBu) return [];
    return businessUnits.filter((bu) => selectedIntegrationForBu.businessUnitIds.includes(bu.id));
  }, [businessUnits, selectedIntegrationForBu]);

  // Track previous initialIntegrationId to detect actual changes
  const prevInitialIntegrationIdRef = useRef<number | null | undefined>(initialIntegrationId);

  // Update selected integration when initialIntegrationId changes (from parent)
  useEffect(() => {
    const prevInitialIntegrationId = prevInitialIntegrationIdRef.current;
    // Only update if initialIntegrationId actually changed
    if (initialIntegrationId !== prevInitialIntegrationId) {
      if (initialIntegrationId !== null) {
        // Set to new value
        setSelectedIntegrationId(initialIntegrationId);
        setPage(1);
      } else if (prevInitialIntegrationId !== null && prevInitialIntegrationId !== undefined) {
        // Only reset if it changed from a value to null (not on initial mount)
        setSelectedIntegrationId(null);
        setPage(1);
      }
      prevInitialIntegrationIdRef.current = initialIntegrationId;
    }
  }, [initialIntegrationId]);

  // Query for paginated table data (server-side filtering + search, max 50 per page)
  const { data: executionsData, isLoading } = useGetIntegrationExecutionsQuery(
    {
      id: selectedIntegrationId ?? 0,
      page,
      limit: MAX_REQUESTS_PER_PAGE,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      statusCode: statusCodeFilter ?? undefined,
      startDate,
      businessUnitId: businessUnitFilter ?? undefined,
      search: debouncedSearch || undefined,
    },
    {
      skip: !selectedIntegrationId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Summary stats for the window/BU come from a server-side aggregate (two indexed COUNTs),
  // so we never pull the underlying rows to the client just to reduce them.
  const { data: summaryStats } = useGetIntegrationStatsQuery(
    {
      id: selectedIntegrationId ?? 0,
      startDate,
      businessUnitId: businessUnitFilter ?? undefined,
    },
    {
      skip: !selectedIntegrationId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Aggregated error signatures for fast triage.
  const { data: errorSummaryData } = useGetIntegrationErrorSummaryQuery(
    {
      id: selectedIntegrationId ?? 0,
      startDate,
      businessUnitId: businessUnitFilter ?? undefined,
      limit: 6,
    },
    {
      skip: !selectedIntegrationId,
      refetchOnMountOrArgChange: true,
    }
  );

  // The list endpoint strips input/output/headers to stay under the body cap, so fetch the
  // full payload lazily when a row is opened.
  const { data: executionDetail, isFetching: isDetailFetching } = useGetIntegrationExecutionDetailQuery(
    { id: selectedIntegrationId ?? 0, executionId: selectedExecution?.id ?? 0 },
    { skip: !selectedIntegrationId || !selectedExecution }
  );

  const selectedIntegration = integrations.find((int) => int.id === selectedIntegrationId);

  const calculatedStats = summaryStats ?? {
    totalExecutions: 0,
    successCount: 0,
    errorCount: 0,
    successRate: null,
  };

  // Pagination is driven by the server's filtered total (search + filters paginate the full set).
  const filteredTotal = executionsData?.pagination.total ?? 0;
  const totalPages = executionsData?.pagination.totalPages ?? 0;

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  const executionsToShow = executionsData?.executions ?? [];

  // Drill into a specific error signature from the summary panel.
  const handleErrorGroupClick = (statusCode: number | null, errorText: string | null) => {
    setStatusFilter('error');
    if (statusCode !== null) {
      setStatusCodeFilter(statusCode);
    } else if (errorText) {
      setSearchInput(errorText);
    }
  };

  const resetFiltersOnIntegrationChange = () => {
    setStatusFilter('all');
    setStatusCodeFilter(null);
    setBusinessUnitFilter(null);
    setTimeWindow('all');
    setSearchInput('');
    setPage(1);
  };

  return (
    <Box>
      {/* Recent Requests Summary */}
      {selectedIntegrationId && selectedIntegration && calculatedStats.totalExecutions > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: RADIUS_CARD,
            bgcolor: SURFACE_CONTAINER_LOW,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: alpha(NAVY, 0.6),
              fontWeight: 600,
            }}
          >
            Resumen de las últimas {calculatedStats.totalExecutions} solicitudes
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                Ejecuciones:{' '}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                >
                  {calculatedStats.totalExecutions}
                </Typography>
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                Éxitos:{' '}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: 'success.main',
                    fontWeight: 600,
                  }}
                >
                  {calculatedStats.successCount}
                </Typography>
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                Errores:{' '}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: 'error.main',
                    fontWeight: 600,
                  }}
                >
                  {calculatedStats.errorCount}
                </Typography>
              </Typography>
            </Box>
            {calculatedStats.successRate !== null && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: `${getSuccessRateColor(calculatedStats.successRate)}.main`,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  Tasa de Éxito:{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    color={`${getSuccessRateColor(calculatedStats.successRate)}.main`}
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {calculatedStats.successRate.toFixed(1)}%
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: statusCodeFilter !== null ? 1.5 : 3,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          pb: 0,
          pt: 0.8,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200, maxWidth: 250, ...filterControlSx }}>
          <InputLabel>Integración</InputLabel>
          <Select
            value={selectedIntegrationId || ''}
            onChange={(e) => {
              setSelectedIntegrationId(Number(e.target.value));
              resetFiltersOnIntegrationChange();
            }}
            label="Integración"
            sx={{ height: '100%' }}
          >
            {integrations.map((integration) => (
              <MenuItem key={integration.id} value={integration.id}>
                {integration.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120, maxWidth: 150, ...filterControlSx }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'error')}
            label="Status"
            sx={{ height: '100%' }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="success">Éxito</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130, maxWidth: 160, ...filterControlSx }}>
          <InputLabel>Ventana</InputLabel>
          <Select
            value={timeWindow}
            onChange={(e) => setTimeWindow(e.target.value as TimeWindow)}
            label="Ventana"
            sx={{ height: '100%' }}
          >
            <MenuItem value="all">Todo</MenuItem>
            <MenuItem value="1h">Última hora</MenuItem>
            <MenuItem value="24h">Últimas 24h</MenuItem>
            <MenuItem value="7d">Últimos 7 días</MenuItem>
          </Select>
        </FormControl>

        {availableBusinessUnits.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 160, maxWidth: 220, ...filterControlSx }}>
            <InputLabel>Business Unit</InputLabel>
            <Select
              value={businessUnitFilter ?? ''}
              onChange={(e) => setBusinessUnitFilter(String(e.target.value) === '' ? null : Number(e.target.value))}
              label="Business Unit"
              sx={{ height: '100%' }}
            >
              <MenuItem value="">Todas</MenuItem>
              {availableBusinessUnits.map((bu) => (
                <MenuItem key={bu.id} value={bu.id}>
                  {bu.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Buscar"
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Endpoint, ID o texto del error..."
          sx={{ minWidth: 220, maxWidth: 280, ...filterControlSx }}
          disabled={!selectedIntegrationId}
        />
      </Box>
      {/* Active statusCode filter chip */}
      {statusCodeFilter !== null && (
        <Box sx={{ mb: 3 }}>
          <Chip
            label={`Código HTTP: ${statusCodeFilter}`}
            onDelete={() => setStatusCodeFilter(null)}
            size="small"
            sx={{
              bgcolor: alpha(statusCodeColor(statusCodeFilter), 0.15),
              color: statusCodeColor(statusCodeFilter),
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
            }}
          />
        </Box>
      )}
      {/* Error signatures for fast triage */}
      {selectedIntegrationId && (
        <ErrorSummaryPanel errors={errorSummaryData?.errors ?? []} onSelect={handleErrorGroupClick} />
      )}
      {/* Show message if no integration is selected */}
      {!selectedIntegrationId && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: alpha(NAVY, 0.6),
            }}
          >
            Selecciona una integración para ver los detalles
          </Typography>
        </Box>
      )}
      {/* Executions Table */}
      {selectedIntegrationId && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: alpha(NAVY, 0.6),
              mb: 1,
            }}
          >
            {filteredTotal} {filteredTotal === 1 ? 'ejecución' : 'ejecuciones'}
          </Typography>
          <TableContainer
            sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, boxShadow: SHADOW, p: 1 }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>ID</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Código</TableCell>
                  <TableCell sx={headCellSx}>Endpoint</TableCell>
                  <TableCell sx={headCellSx}>Execution Time</TableCell>
                  <TableCell sx={headCellSx}>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ ...bodyCellSx }}>
                      <Typography sx={{ color: NAVY }}>Cargando...</Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && (!executionsToShow || executionsToShow.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ ...bodyCellSx }}>
                      <Typography
                        sx={{
                          color: alpha(NAVY, 0.6),
                        }}
                      >
                        No se encontraron ejecuciones
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  executionsToShow?.map((execution) => (
                    <TableRow
                      key={execution.id}
                      onClick={() => setSelectedExecution(execution)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.18s ease',
                        '&:hover': { bgcolor: SURFACE_HOVER },
                      }}
                    >
                      <TableCell sx={bodyCellSx}>{execution.id}</TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          label={execution.status}
                          color={execution.status === 'success' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: statusCodeColor(execution.statusCode),
                          }}
                        >
                          {execution.statusCode ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: NAVY,
                          }}
                        >
                          {execution.method && `${execution.method} `}
                          {execution.endpoint}
                        </Typography>
                        {execution.error && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'error.main',
                              display: 'block',
                            }}
                          >
                            {execution.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body2" color={getExecutionTimeColor(execution.executionTime)}>
                          {execution.executionTime}ms
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body2" sx={{ color: NAVY }}>
                          {new Date(execution.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {/* Pagination */}
      {selectedIntegrationId && totalPages >= 1 && (
        <Box sx={{ p: 2, borderRadius: RADIUS_CARD, bgcolor: SURFACE_CONTAINER_LOW, mt: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_event, newPage) => setPage(newPage)}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Stack>
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: alpha(NAVY, 0.6),
              }}
            >
              Página {page} de {totalPages} ({(page - 1) * MAX_REQUESTS_PER_PAGE + 1} -{' '}
              {Math.min(page * MAX_REQUESTS_PER_PAGE, filteredTotal)} de {filteredTotal} solicitudes)
            </Typography>
          </Box>
        </Box>
      )}
      <ExecutionDetailDrawer
        execution={selectedExecution}
        detail={executionDetail && executionDetail.id === selectedExecution?.id ? executionDetail : null}
        isDetailLoading={isDetailFetching}
        open={!!selectedExecution}
        onClose={() => setSelectedExecution(null)}
      />
    </Box>
  );
};

export default ExecutionLogs;
